import React, { ReactNode, Dispatch, SetStateAction } from "react";

import algoliarecommend, { RecommendClient } from "@algolia/recommend";
import { algoliasearch, SearchClient } from "algoliasearch";

import { ContentfulProduct } from "@/types/common";
import { segmentEvent } from "@/utils/segmentfunctions";

interface ResultSearchVisible {
  product: boolean;
  article: boolean;
}

export interface SearchContextType {
  searchClient: SearchClient | null;
  recommendClient: RecommendClient | null;
  contentfulProducts: ContentfulProduct[];
  isSearchVisible: boolean;
  setIsSearchVisible: Dispatch<SetStateAction<boolean>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchState: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSearchState: Dispatch<SetStateAction<any>>;
  resultSearchVisible: ResultSearchVisible;
  setResultSearchVisible: Dispatch<SetStateAction<ResultSearchVisible>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultFAQs: any[];
  defaultFAQsLoading: boolean;
  subscriptionsAndOrders: Record<string, string[]>;
  setSubscriptionsAndOrders: Dispatch<SetStateAction<Record<string, string[]>>>;
  resultsFound: number;
  setResultsFound: Dispatch<SetStateAction<number>>;
  searchScrollPosition: number;
  setSearchScrollPosition: Dispatch<SetStateAction<number>>;
  openSearchDropdown: () => void;
  closeSearchDropdown: (runSegmentEvent?: boolean) => void;
  toggleSearchDropdown: () => void;
}

// Create context with null default and assert type with ! where used, or use default values
const SearchContext = React.createContext<SearchContextType | null>(null);

interface SearchProviderProps {
  children: ReactNode;
  contentfulProducts: ContentfulProduct[];
}

const resultSearchDefault: ResultSearchVisible = {
  product: false,
  article: false,
};

const SearchProvider = ({ children, contentfulProducts }: SearchProviderProps) => {
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [searchClient, setSearchClient] = React.useState<SearchClient | null>(null);
  const [recommendClient, setRecommendClient] = React.useState<RecommendClient | null>(null);
  const [isSearchVisible, setIsSearchVisible] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [searchState, setSearchState] = React.useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [defaultFAQs, setDefaultFAQs] = React.useState<any[]>([]);
  const [defaultFAQsLoading, setDefaultFAQsLoading] = React.useState(true);
  const [subscriptionsAndOrders, setSubscriptionsAndOrders] = React.useState<
    Record<string, string[]>
  >({});
  const [resultsFound, setResultsFound] = React.useState(0);
  const [searchScrollPosition, setSearchScrollPosition] = React.useState(0);

  React.useEffect(() => {
    // Algolia Search
    const search_client = algoliasearch(
      process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!,
      process.env.NEXT_PUBLIC_ALGOLIA_API_KEY!
    );
    setSearchClient(search_client);

    // Algolia Recommend
    const recommend_client = algoliarecommend(
      process.env.NEXT_PUBLIC_ALGOLIA_APP_ID!,
      process.env.NEXT_PUBLIC_ALGOLIA_API_KEY!
    );
    setRecommendClient(recommend_client);

    // Get all contentful products if needed
  }, []);

  React.useEffect(() => {
    const zendeskIndexName =
      process.env.NEXT_PUBLIC_ALGOLIA_ZENDESK_INDEX_NAME ?? "Zendesk_production";
    const getDefaultFAQs = async () => {
      if (searchClient) {
        try {
          const relatedFAQs = await searchClient.getObjects({
            requests: [
              { indexName: zendeskIndexName, objectID: "28789023418259" },
              { indexName: zendeskIndexName, objectID: "29416479957395" },
              { indexName: zendeskIndexName, objectID: "29416668240915" },
            ],
          });
          setDefaultFAQs(relatedFAQs?.results || []);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Algolia API error related articles ${error}`);
        } finally {
          setDefaultFAQsLoading(false);
        }
      }
    };
    getDefaultFAQs();
  }, [searchClient]);

  const [resultSearchVisible, setResultSearchVisible] =
    React.useState<ResultSearchVisible>(resultSearchDefault);

  const openSearchDropdown = React.useCallback(() => {
    setSearchScrollPosition(window.scrollY);
    setIsSearchVisible(true);

    if (searchState) {
      segmentEvent("Search Modal Opened", {
        name: "Search",
        location: "navigation",
        state: searchState,
      });
    }
  }, [searchState]);

  React.useEffect(() => {
    if (searchState && initialLoad) {
      segmentEvent("Search Modal Opened", {
        name: "Search",
        location: "navigation",
        state: searchState,
      });
      setInitialLoad(false);
    }
  }, [searchState, initialLoad]);

  const closeSearchDropdown = React.useCallback(
    (runSegmentEvent = true) => {
      setIsSearchVisible(false);
      setResultSearchVisible(resultSearchDefault);
      setSearch("");
      if (runSegmentEvent) {
        segmentEvent("Search Modal Closed", {
          name: "Search",
          location: "navigation",
          state: searchState,
        });
      }
    },
    [searchState]
  );

  const toggleSearchDropdown = React.useCallback(() => {
    if (!isSearchVisible) {
      openSearchDropdown();
    } else {
      closeSearchDropdown();
    }
  }, [isSearchVisible, openSearchDropdown, closeSearchDropdown]);

  const value: SearchContextType = {
    searchClient,
    recommendClient,
    contentfulProducts,
    isSearchVisible,
    setIsSearchVisible,
    search,
    setSearch,
    searchState,
    setSearchState,
    resultSearchVisible,
    setResultSearchVisible,
    defaultFAQs,
    defaultFAQsLoading,
    subscriptionsAndOrders,
    setSubscriptionsAndOrders,
    resultsFound,
    setResultsFound,
    searchScrollPosition,
    setSearchScrollPosition,
    openSearchDropdown,
    closeSearchDropdown,
    toggleSearchDropdown,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export default SearchContext;
export { SearchProvider };
